import { useParams } from "react-router-dom";
import SuccessCheck from "../../assets/images/success-check.svg";
import PageLayout from "../pageLayout";
import printer from "../../assets/images/printer.svg";
import roundTick from "../../assets/images/round-tick.svg";
import constants from "./constants.json";
import { useAppDispatch, useAppSelector } from "../../hooks";
import "./style.css";
import { useEffect, useState } from "react";
import { PageLoader } from "../../components/loaders/pageLoader";
import paymentService from "../../services/paymentService";
import { selectStoreConfigSelector } from "../../store/storeConfig/selector";
import {
  OrderInfoItemState,
  OrderInformationState,
} from "../../utils/interfaces/payment";
import { selectCurrencySelector } from "../../store/currency/selector";
import PackageInformationView, { PackageInfoState } from "../cart/packageView";
import UserInformationView, { UserDetailsState } from "../cart/userInformation";
import { addDataLayer, formatDecimalsWithCommas, mtm_clearUtmParamsFromSession } from "../../utils/utility";
import { notificationActionDispatch } from "../../store/notification/actions";
import { router } from "../../utils/router";
import { baseRouterPath } from "../../utils/constants";
import { setOrderId } from "../../services/cartService";
import { purchaseEventDL } from "../../utils/dataLayerManager";

const PaymentSuccess = () => {
  const [loader, setLoader] = useState({ cart: true, order: true });
  const [order, setOrderData] = useState({} as OrderInformationState);
  const [suffix, setSuffix] = useState({} as { [key: string]: string });

  const { setNotification } = notificationActionDispatch(useAppDispatch());

  const currency = useAppSelector(selectCurrencySelector);
  const storeConfig = useAppSelector(selectStoreConfigSelector);
  const { orderid } = useParams<{ orderid: string }>();
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let temp = {};
    if (storeConfig && storeConfig.subscription_type) {
      storeConfig?.subscription_type.split(",").map((type: string) => {
        Object.keys(storeConfig).map((key) => {
          if (key.includes("suffix") && key.includes(type.toLowerCase())) {
            temp = {
              ...temp,
              [type]: storeConfig[key as keyof typeof storeConfig],
            }; // Use keyof typeof to access the correct type
          }
        });
      });
      setSuffix(temp);
    }
  }, [storeConfig]);

  const fetchData = async () => {
    setOrderId("");
    await paymentService
      .fetchOrderDetails(orderid || "")
      .then((res) => {
        setLoader((prevState) => ({ ...prevState, order: false }));
        if (res) {
          addDataLayer(purchaseEventDL(res), "purchase");
          setOrderData(res);
          mtm_clearUtmParamsFromSession();
        }
      })
      .catch((err) => {
        setLoader((prevState) => ({ ...prevState, order: false }));
        setNotification({ message: err.message, type: "error" });
        router.navigate(baseRouterPath);
      });
  };

  const setPackageInformation = (items: Array<OrderInfoItemState>) => {
    const arr = [] as Array<PackageInfoState>;
    items.map((item: OrderInfoItemState) => {
      if (item)
        arr.push({
          name: item.title,
          description: item?.short_discription,
          features: item?.ln_sl_inclusion_overview?.split(",") || [],
        });
    });
    return arr;
  };

  const setUserData = () => {
    const obj = {} as UserDetailsState;
    obj.firstname = order.billing[0].name || "";
    obj.email = order.purchaser_email || "";
    obj.telephone = order.billing[0].telephone || "";
    obj.jobTitle = order.ln_job_title || "";
    obj.street = order.billing[0].street || "";
    obj.city = order.billing[0].city || "";
    obj.region = order.billing[0].region || "";
    obj.postcode = order.billing[0].postcode || "";
    obj.lnfe = JSON.parse(order.fe_info);
    obj.attorneySize = order.no_attorney || 1;
    return obj;
  };

  const printPage = () => {
    addDataLayer({ event: "print_page" }, "print_page");
    window.print();
  };

  const getSuffix = () => {
    if (order?.ln_sl_subscription_type.toLowerCase().includes("month"))
      return storeConfig.price_suffix_monthly
    else return storeConfig.price_suffix_yearly;
  }

  return (
    <PageLayout>
      {!loader.order ? (
        <>
          <div className="payment-success-container p-4">
            <button
              id="print-btn"
              data-track="print-button"
              onClick={printPage}
              className="font-bold text-grey-scale-slate flex mb-2"
            >
              <img src={printer} alt="Printer" className="mr-2" />
              Print page
            </button>
            <div className="payment-success-content">
              <img
                className="success-check-container"
                src={SuccessCheck}
                alt="Success Check"
              />
              <h1 className="text-[30px] font-bold my-2">
                {constants.heading}
              </h1>
              <p className="text-grey-scale-slate text-lg my-2 pii-mask">
                {constants.description.replace("%d", orderid || "")}
              </p>
              <div className="block rounded-t-lg text-grey-scale-slate">
                <h1 className="text-xl font-bold text-blues-ink mb-4">
                  {constants.subsDetails.heading}
                </h1>
                <div className="flex mb-4">
                  <img className="mr-4" src={roundTick} alt="Tick" />
                  <h1 className="font-semibold ">
                    {constants.subsDetails.subsLength} : {order.contract_length}{" "}
                    {getSuffix()}
                  </h1>
                </div>
                <div className="flex mb-4">
                  <img className="mr-4" src={roundTick} alt="Tick" />
                  <h1 className="font-semibold">
                    {constants.subsDetails.attorneySize} : {order.no_attorney}
                  </h1>
                </div>
              </div>
              <div className="block border-t-0 text-grey-scale-slate">
                <h1 className="text-xl font-bold text-blues-ink mb-4">
                  {constants.payment.heading}
                </h1>
                <div className="md:flex md:justify-between md:flex-col lg:flex-row">
                  <div className="md:w-full lg:w-1/2 px-4">
                    <h1 className="font-bold text-blues-ink">
                      {constants.payment.amount}:
                    </h1>
                    <div className="grid">
                      {order.items.map((item, index) => (
                        <div
                          key={`ordered-item-${index}`}
                          className="my-2 order-items"
                        >
                          <span className="order-item__name">{item.title}</span>
                          <span className="order-item__price">
                            <span className="font-bold ">
                              {currency.base_currency_symbol}
															{formatDecimalsWithCommas(item.price)}
                            </span>
                            /{getSuffix()}
                          </span>
                        </div>
                      ))}
                    </div>
                    <hr className="text-grey-scale-silver my-4" />

                    <div className="grid">
                      <div className="order-items my-2">
                        <span className="order-item__name">
                          {constants.payment.subTotal}
                        </span>
                        <span className="order-item__price">
                          <span className="font-bold">
                            {currency.base_currency_symbol}
														{formatDecimalsWithCommas(order.sub_total_with_discount)}
                          </span>
                          /{getSuffix()}
                        </span>
                      </div>

                      {order.sub_total !== order.sub_total_with_discount && (
                        <div className="order-items w-full my-2 line-through flex-row-reverse ">
                          <span className="order-item__price">
                            <span className="font-bold">
                              {currency.base_currency_symbol}
                              {formatDecimalsWithCommas(order.sub_total)}
                            </span>
                            /{getSuffix()}
                          </span>
                        </div>
                      )}

                      <div className="order-items my-2">
                        <span className="order-item__name">
                          {constants.payment.tax}
                        </span>
                        <span className="order-item__price">
                          <span className="font-bold">
                            {currency.base_currency_symbol}
                            {formatDecimalsWithCommas(order.tax_amount)}
                          </span>
                          /{getSuffix()}
                        </span>
                      </div>
                    </div>

                    <hr className="text-grey-scale-silver my-4" />

                    <div className="grid">
                      <div className="order-items my-2">
                        <span className="order-item__name">
                          {constants.payment.total}
                        </span>
                        <span className="order-item__price">
                          <span className="font-bold">
                            {currency.base_currency_symbol}
                            {formatDecimalsWithCommas(order.grand_total)}
                          </span>
                          /{getSuffix()}
                        </span>
                      </div>
                      {/* <div className='text-sm my-4 text-center' dangerouslySetInnerHTML={{__html: storeConfig.tax_disclaimer_message}} /> */}
                      <div className="order-items my-2">
                        <h1 className="order-item__name text-blues-ink text-bold">
                          <b>
                            {constants.payment.frequency}:{" "}
                            <span className="order-item__price">
                              {order?.ln_sl_subscription_type}
                            </span>
                          </b>
                        </h1>
                      </div>
                    </div>
                  </div>
                  <div className="md:w-full lg:w-1/2 px-4">
                    <h1 className="font-bold text-blues-ink mb-4 pii-mask">
                      {constants.payment.card.heading}
                    </h1>
                    <div className="flex">
                      <div className="w-[170px] h-[100px] bg-blues-midnight rounded mr-4 flex flex-col p-2 text-white text-[10px]">
                        <div className="flex h-1/2 justify-start items-end pii-mask">
                          {order.masked_cc_number}
                        </div>
                        <div className="flex justify-between items-end h-1/2">
                          <span className="pii-mask">
                            {order.payment_card_expiration}
                          </span>
                          <span className="pii-mask">
                            {order.payment_card_type}
                          </span>
                        </div>
                      </div>
                      <div>
                        <h1 className="font-bold text-blues-ink pii-mask">
                          {constants.payment.card.subHeading}
                        </h1>
                        <h1 className="font-bold text-blues-ink pii-mask">
                          {order.billing[0].name},
                        </h1>
                        <p className="pii-mask">{order.billing[0].street}, {order.billing[0].city},</p>
                        <p className="pii-mask">
                           {order.billing[0].region}  {order.billing[0].postcode}.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="block border-t-0 rounded-b-xl">
                <h1 className="text-xl font-bold text-blues-ink ">
                  {constants.payment.userInformation}
                </h1>
                {UserInformationView(setUserData(), "col")}
              </div>
            </div>
            <div className="payment-success-content mt-10">
              <h1 className="text-left text-[25px] font-bold my-2 w-full">
                {constants.package.heading}
              </h1>
              <h1 className="text-left text-[18px] my-2 w-full">
                {constants.package.subheading}
              </h1>
              {PackageInformationView(setPackageInformation(order.items))}
            </div>
          </div>
        </>
      ) : (
        <PageLoader />
      )}
    </PageLayout>
  );
};

export default PaymentSuccess;
